import { useState } from "react";
import { toast } from "sonner";
import { Plus, X } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { api, formatApiError } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { inputCls } from "@/components/hub/Common";

const TYPES = ["new", "improved", "fix"];
export const blankRelease = (version = "") => ({ version, title: "", summary: "", published: true, items: [{ type: "new", text: "" }] });

export const ReleaseForm = ({ initial, id, onSaved, onCancel }) => {
  const { t } = useI18n();
  const [f, setF] = useState(initial);
  const setItem = (i, patch) => setF({ ...f, items: f.items.map((it, j) => (j === i ? { ...it, ...patch } : it)) });
  const submit = async (e) => {
    e.preventDefault();
    const body = { ...f, items: f.items.filter((it) => it.text.trim()) };
    try {
      if (id) await api.patch(`/admin/releases/${id}`, body); else await api.post("/admin/releases", body);
      toast.success(t("adm.saved"));
      onSaved();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <form onSubmit={submit} className="cx-card cx-brackets rounded-2xl p-6 space-y-4" data-testid="release-form">
      <div className="font-display text-lg text-white">{id ? `${t("adm.rel_edit")} v${initial.version}` : t("adm.rel_new")}</div>
      <div className="grid sm:grid-cols-[140px_1fr] gap-3">
        <input data-testid="release-version-input" className={`${inputCls} font-mono`} placeholder={t("adm.rel_version")} value={f.version} onChange={(e) => setF({ ...f, version: e.target.value.trim() })} required pattern="\d+\.\d+\.\d+" />
        <input data-testid="release-title-input" className={inputCls} placeholder={t("adm.rel_title")} value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} required minLength={2} />
      </div>
      <textarea data-testid="release-summary-input" className={`${inputCls} min-h-[70px]`} placeholder={t("adm.rel_summary")} value={f.summary} onChange={(e) => setF({ ...f, summary: e.target.value })} />
      <div className="cx-label">{t("adm.rel_items")}</div>
      {f.items.map((it, i) => (
        <div key={i} className="flex gap-2 items-center" data-testid={`release-item-row-${i}`}>
          <select data-testid={`release-item-type-${i}`} value={it.type} onChange={(e) => setItem(i, { type: e.target.value })} className="rounded-lg bg-[#070912] border border-white/10 px-2 py-3 text-xs text-white outline-none">
            {TYPES.map((x) => <option key={x} value={x}>{t(`rel.type_${x}`)}</option>)}
          </select>
          <input data-testid={`release-item-text-${i}`} className={inputCls} placeholder={t("adm.rel_item_ph")} value={it.text} onChange={(e) => setItem(i, { text: e.target.value })} />
          {f.items.length > 1 && <button type="button" data-testid={`release-item-remove-${i}`} onClick={() => setF({ ...f, items: f.items.filter((_, j) => j !== i) })} className="text-slate-500 hover:text-rose-400 p-2"><X className="w-4 h-4" /></button>}
        </div>
      ))}
      <button type="button" data-testid="release-add-item-btn" onClick={() => setF({ ...f, items: [...f.items, { type: "new", text: "" }] })} className="cx-ghost rounded-full px-4 py-2 text-xs flex items-center gap-1.5"><Plus className="w-3.5 h-3.5" />{t("adm.rel_add_item")}</button>
      <div className="flex flex-wrap items-center gap-4 pt-2">
        <label className="flex items-center gap-2 text-sm text-slate-300"><Switch data-testid="release-published-switch" checked={f.published} onCheckedChange={(v) => setF({ ...f, published: v })} />{f.published ? t("adm.rel_published") : t("adm.rel_draft")}</label>
        <div className="ml-auto flex gap-2">
          {onCancel && <button type="button" data-testid="release-cancel-btn" onClick={onCancel} className="cx-ghost rounded-full px-5 py-2.5 text-sm">{t("adm.rel_cancel")}</button>}
          <button type="submit" data-testid="release-submit-btn" className="cx-btn rounded-full px-6 py-2.5 text-sm font-semibold">{id ? t("adm.rel_save") : t("adm.rel_publish")}</button>
        </div>
      </div>
    </form>
  );
};
